import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XtreamContent } from '../../api/xtreamAPI';
import { useFavorites } from '../../hooks/useFavorites';
import './ChannelList.css';

/** Must match --channel-item-height in tokens.css */
const ITEM_H = 64;
/** Scroll container height = channel-list height (492) minus header (44) */
const CONTAINER_H = 448;
/** Extra rows to render above/below visible window */
const BUFFER = 5;

interface ChannelListProps {
  channels: XtreamContent[];
  focusedIndex?: number;
  favoriteShortcutCount?: number;
  onSelect: (channel: XtreamContent) => void;
  loading?: boolean;
}

interface ChannelItemProps {
  channel: XtreamContent;
  isSelected: boolean;
  isFavorite: boolean;
  sectionStart: boolean;
  onClick: () => void;
}

const ChannelItem: React.FC<ChannelItemProps> = ({ channel, isSelected, isFavorite, sectionStart, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const iconUrl = channel.icon || channel.stream_icon || '';

  return (
    <button
      className={`channel-item${isSelected ? ' channel-item--selected' : ''}${isFavorite ? ' channel-item--favorite' : ''}${sectionStart ? ' channel-item--section-start' : ''}`}
      onClick={onClick}
      type="button"
      title={channel.name}
    >
      <div className="channel-item__logo">
        {iconUrl && !imgError ? (
          <img
            src={iconUrl}
            alt={channel.name}
            loading="lazy"
            onError={() => setImgError(true)}
            className="channel-item__logo-img"
          />
        ) : (
          <div className="channel-item__logo-placeholder" aria-hidden="true" />
        )}
      </div>
      <span className="channel-item__name">{channel.name}</span>
      {isFavorite && <span className="channel-item__fav-dot" aria-hidden="true" />}
    </button>
  );
};

export const ChannelList: React.FC<ChannelListProps> = ({
  channels,
  focusedIndex = 0,
  favoriteShortcutCount = 0,
  onSelect,
  loading,
}) => {
  const { getFavoritesByType } = useFavorites();
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  // Set de IDs favoritos para lookup rápido
  const favoriteIds = useMemo(() => {
    const set = new Set<string>();
    getFavoritesByType('live').forEach((f) => set.add(f.id));
    return set;
  }, [getFavoritesByType]);

  // Auto-scroll ao item selecionado (sem scrollIntoView — o elemento pode não estar montado)
  useEffect(() => {
    if (!listRef.current) return;
    const itemTop = focusedIndex * ITEM_H;
    const itemBottom = itemTop + ITEM_H;
    const currentTop = listRef.current.scrollTop;
    const visibleBottom = currentTop + CONTAINER_H;
    if (itemTop < currentTop || itemBottom > visibleBottom) {
      const newScrollTop = itemTop - CONTAINER_H / 2 + ITEM_H / 2;
      listRef.current.scrollTop = newScrollTop;
      // Sync state so the virtual window re-renders around the new position.
      // Setting scrollTop programmatically does NOT fire the React onScroll event.
      setScrollTop(newScrollTop);
    }
  }, [focusedIndex]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop((e.currentTarget as HTMLDivElement).scrollTop);
  }, []);

  // Virtual window
  const startIdx = Math.max(0, Math.floor(scrollTop / ITEM_H) - BUFFER);
  const endIdx = Math.min(channels.length - 1, Math.ceil((scrollTop + CONTAINER_H) / ITEM_H) + BUFFER);
  const visibleChannels = channels.slice(startIdx, endIdx + 1);
  const topPad = startIdx * ITEM_H;
  const bottomPad = Math.max(0, (channels.length - endIdx - 1)) * ITEM_H;

  return (
    <div className="channel-list">
      <div className="channel-list__header">
        <span className="channel-list__header-text">{t('liveTV.recentAndFavorites')}</span>
      </div>

      <div className="channel-list__scroll" ref={listRef} onScroll={handleScroll}>
        {loading && (
          <div className="channel-list__loading">{t('liveTV.loadingChannels')}</div>
        )}
        {!loading && channels.length === 0 && (
          <div className="channel-list__empty">{t('liveTV.noChannels')}</div>
        )}
        {!loading && channels.length > 0 && (
          <>
            {topPad > 0 && <div style={{ height: topPad, flexShrink: 0 }} />}
            {visibleChannels.map((ch, visibleIndex) => {
              const index = startIdx + visibleIndex;
              const id = String(ch.stream_id || '');
              return (
                <ChannelItem
                  key={`${index}-${id}-${ch.name}`}
                  channel={ch}
                  isSelected={focusedIndex === index}
                  isFavorite={favoriteIds.has(id)}
                  sectionStart={favoriteShortcutCount > 0 && index === favoriteShortcutCount}
                  onClick={() => onSelect(ch)}
                />
              );
            })}
            {bottomPad > 0 && <div style={{ height: bottomPad, flexShrink: 0 }} />}
          </>
        )}
      </div>
    </div>
  );
};
